import { memo, useMemo, useRef } from 'react'

import * as SCBase from '../styles'
import { IconName, IconSize, Popper } from 'ui/components'
import { TableButton } from 'ui/components/Buttons'
import { InputHint, Required } from 'ui/core'

import { ChoiceValue } from '../../../types'
import { SELECT_PLACEHOLDER, SELECT_PLACEHOLDER_ADD_OPTIONS, SELECT_PLACEHOLDER_SEARCHABLE } from '../constants'
import { useSelect } from '../hooks/useSelect'
import { MultiSelectProps } from '../types'
import { Option } from './Option'

import * as SC from './styles'

export const ColumnMultiSelect = memo((props: MultiSelectProps) => {
  const {
    searchable = false,
    value,
    onChange,
    required = false,
    error,
    data = [],
    serverOptions,
    placeholder,
    disabled = false,
    hint,
    hideHint,
    'data-qa': dataQa,
    enableSelectAll = false,
    ...rest
  } = props

  const listRef = useRef<(HTMLDivElement | null)[]>([])
  const inputRef = useRef(null)
  const dropDownRef = useRef<HTMLDivElement | null>(null)

  const selectionCallback = (values: ChoiceValue[]) => {
    if (onChange) {
      onChange(values)
    }
  }

  const {
    open,
    modelData: items,
    count,
    isSearching,
    isShowRequired,
    isEmpty,
    activeIndex,
    handleClose,
    handleOpen,
    selectValue,
    unselectValue,
    getReferenceProps,
    getOptionProps,
    getInputProps,
    inputValue,
    selectAllValues,
    unselectAllValues
  } = useSelect({
    selectedValues: (value as ChoiceValue[]) ?? [],
    open: false,
    data,
    required,
    disabled,
    error: !!error,
    listRef,
    inputRef,
    serverOptions,
    mode: 'columns',
    searchable,
    selectionCallback
  })

  const selectedItems = useMemo(() => items.filter((el) => el.selected), [items])
  const notSelectedItems = useMemo(() => items.filter((el) => !el.selected), [items])

  const onSelectAll = () => {
    selectAllValues(notSelectedItems.map((el) => el.data.value))
    if (dropDownRef?.current) {
      dropDownRef.current.focus()
    }
  }

  const onUnselectAll = () => {
    unselectAllValues()
    if (dropDownRef?.current) {
      dropDownRef.current.focus()
    }
  }

  const renderOptions = () => (
    <SC.Columns>
      <div>
        <SC.DropDownHeader>
          <strong>Enabled options</strong>
          {enableSelectAll && <TableButton onClick={onSelectAll}>Select All</TableButton>}
        </SC.DropDownHeader>
        {notSelectedItems.length === 0 && <SC.DropDownSubHeader>No more items</SC.DropDownSubHeader>}
        <SC.OptionsList data-qa={dataQa ? `${dataQa} - Enabled options` : undefined}>
          {notSelectedItems.map(
            (item) =>
              item !== undefined && (
                <Option
                  key={item.index}
                  ref={(node) => (listRef.current[item.index] = node)}
                  label={item.data.label}
                  value={item.data.value}
                  disabled={item.data.disabled}
                  title={item.data.title}
                  selected={item.selected}
                  active={item.index === activeIndex}
                  onClick={() => selectValue(item.data.value)}
                  data-qa={dataQa ? `${dataQa} - ${item.data.label}` : undefined}
                />
              )
          )}
        </SC.OptionsList>
      </div>
      <div>
        <SC.DropDownHeader>
          <strong>Added options</strong>
          {enableSelectAll && <TableButton onClick={onUnselectAll}>Unselect All</TableButton>}
        </SC.DropDownHeader>
        {selectedItems.length === 0 && <SC.DropDownSubHeader>No selected items yet</SC.DropDownSubHeader>}
        <SC.OptionsList data-qa={dataQa ? `${dataQa} - Added options` : undefined}>
          {selectedItems.map(
            (item) =>
              item !== undefined && (
                <Option
                  key={item.index}
                  ref={(node) => (listRef.current[item.index] = node)}
                  label={item.data.label}
                  value={item.data.value}
                  disabled={item.data.disabled}
                  title={item.data.title}
                  selected={true}
                  active={item.index === activeIndex}
                  onClick={() => unselectValue(item.data.value)}
                  data-qa={dataQa ? `${dataQa} - ${item.data.label}` : undefined}
                />
              )
          )}
        </SC.OptionsList>
      </div>
    </SC.Columns>
  )

  const showSpinner = isSearching && open
  let resultedPlaceholder = placeholder

  if (searchable) {
    resultedPlaceholder = SELECT_PLACEHOLDER_SEARCHABLE
  } else {
    if (count > 0) {
      resultedPlaceholder = SELECT_PLACEHOLDER_ADD_OPTIONS
    } else {
      resultedPlaceholder = placeholder || SELECT_PLACEHOLDER
    }
  }

  return (
    <>
      <Popper
        listRef={listRef}
        opened={open}
        onOpen={handleOpen}
        onClose={handleClose}
        scrollClose={true}
        autoSize={true}
        autoSizeWidth={'double'}
        autoSizeMaxHeight={SC.OVERALL_HEIGHT}
        autoSizeMinHeight={100}
        placement="bottom-start"
        style={SC.PopperStyles()}
        disabled={disabled}
        render={() => (
          <SCBase.DropDown
            tabIndex={-1}
            ref={dropDownRef}
            {...getOptionProps()}
            $isSearching={isSearching}
            $column={false}
          >
            {renderOptions()}
          </SCBase.DropDown>
        )}
      >
        <SCBase.ReferenceOuter
          {...rest}
          role="listbox"
          $isEmpty={isEmpty}
          $error={!!error}
          disabled={disabled}
          {...getReferenceProps()}
        >
          <Required data-testid="required" isInputEmpty={isShowRequired} />
          <SCBase.InputForMultiSelect
            {...getInputProps()}
            $opened={open}
            ref={inputRef}
            type="text"
            value={inputValue}
            data-qa={dataQa}
            data-testid="search-value"
            placeholder={resultedPlaceholder}
            readOnly={!open || !searchable}
            autoComplete="multiselect"
            disabled={disabled}
          />
          {showSpinner && (
            <SCBase.Spinner
              size={16}
              color="default"
              durationInSeconds={1.0}
              data-qa={dataQa ? `${dataQa} - searching spinner` : undefined}
            />
          )}
          {!showSpinner && count > 0 && (
            <SCBase.Counter $error={!!error} data-testid="counter">
              {count}
            </SCBase.Counter>
          )}
          <SCBase.WrappedIcon name={IconName.DOWN} size={IconSize.XS} $open={open} />
        </SCBase.ReferenceOuter>
      </Popper>
      <InputHint data-testid="hint" hint={hint} disabled={disabled} hideHint={hideHint} />
    </>
  )
})
